import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { StoreButtons } from "@/components/shared/StoreButtons"
import { APP_NAME, APP_STORE_URL, PLAY_STORE_URL, BILLING_ENABLED } from "@/lib/appConfig"

function MobileStoreBadges() {
    if (!APP_STORE_URL && !PLAY_STORE_URL) return null
    return (
        <div className="md:hidden flex items-center gap-1.5">
            {APP_STORE_URL && (
                <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity">
                    <img src="/download-from-app-store.svg" alt="Download on the App Store" className="h-7 w-auto" />
                </a>
            )}
            {PLAY_STORE_URL && (
                <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity">
                    <img src="/download-from-play.svg" alt="Get it on Google Play" className="h-7 w-auto" />
                </a>
            )}
        </div>
    )
}
import { cn } from "@/lib/utils"

export default function PublicNavbar() {
    const [scrolled, setScrolled] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const location = useLocation()
    const isToolsActive = location.pathname.startsWith("/tools")

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 20)
        window.addEventListener("scroll", handler, { passive: true })
        return () => window.removeEventListener("scroll", handler)
    }, [])

    return (
        <header
            className={cn(
                "fixed top-0 inset-x-0 z-50 transition-all duration-300",
                scrolled
                    ? "bg-background/90 backdrop-blur-xl border-b border-border/60 shadow-sm"
                    : "bg-transparent"
            )}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2.5 select-none text-foreground hover:opacity-90">
                    <img src="/app-icon.png" className="h-9 w-9 object-contain" alt={APP_NAME} />
                    <span className="font-bold text-xl tracking-tight">{APP_NAME.toUpperCase()}</span>
                </Link>

                {/* Desktop nav — pill style */}
                <nav className="hidden md:flex items-center rounded-full border border-border/60 bg-background/50 backdrop-blur-sm px-2 py-1 gap-0.5 text-sm font-medium text-muted-foreground">
                    <a href="/#features" className="px-3 py-1.5 rounded-full hover:bg-muted hover:text-foreground transition-all">Features</a>
                    <a href="/#how-it-works" className="px-3 py-1.5 rounded-full hover:bg-muted hover:text-foreground transition-all">How it works</a>
                    <Link
                        to="/tools"
                        className={cn(
                            "px-3 py-1.5 rounded-full transition-all",
                            isToolsActive
                                ? "bg-primary/10 text-primary font-semibold"
                                : "hover:bg-muted hover:text-foreground"
                        )}
                    >
                        Free Tools
                    </Link>
                    {BILLING_ENABLED && <a href="/#pricing" className="px-3 py-1.5 rounded-full hover:bg-muted hover:text-foreground transition-all">Pricing</a>}
                    {/* <Link to="/blog" className="px-3 py-1.5 rounded-full hover:bg-muted hover:text-foreground transition-all">Blog</Link> */}
                </nav>

                <div className="hidden md:flex items-center gap-2">
                    <StoreButtons />
                    {(APP_STORE_URL || PLAY_STORE_URL) && <div className="w-px h-5 bg-border/60" />}
                    <Link to="/login">
                        <Button variant="ghost" size="sm" className="text-sm font-medium">Sign In</Button>
                    </Link>
                    <Link to="/register">
                        <Button size="sm" className="text-sm font-semibold rounded-xl shadow-sm">
                            Get Started Free
                        </Button>
                    </Link>
                </div>

                {/* Mobile store badges */}
                <MobileStoreBadges />

                {/* Mobile hamburger */}
                <button
                    className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground"
                    onClick={() => setMobileOpen(!mobileOpen)}
                >
                    {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border px-4 py-4 space-y-3 shadow-lg">
                    <a href="/#features" className="block py-2 text-sm font-medium" onClick={() => setMobileOpen(false)}>Features</a>
                    <a href="/#how-it-works" className="block py-2 text-sm font-medium" onClick={() => setMobileOpen(false)}>How it works</a>
                    <Link to="/tools" className="block py-2 text-sm font-medium" onClick={() => setMobileOpen(false)}>Free Tools</Link>
                    {BILLING_ENABLED && <a href="/#pricing" className="block py-2 text-sm font-medium" onClick={() => setMobileOpen(false)}>Pricing</a>}
                    <div className="pt-2 flex flex-col gap-2">
                        <Link to="/login" onClick={() => setMobileOpen(false)}>
                            <Button variant="outline" className="w-full rounded-xl">Sign In</Button>
                        </Link>
                        <Link to="/register" onClick={() => setMobileOpen(false)}>
                            <Button className="w-full rounded-xl">Get Started Free</Button>
                        </Link>
                    </div>
                </div>
            )}
        </header>
    )
}
